import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/solid-query";
import { axiosInstance } from "@/api/client";
import { PROPOSAL_KEY } from "@/api/query-keys";
import type { ProposalState } from "@/domains/application/constant/status";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import type {
  AcceptProposalResponse,
  ConfirmMeetingResponse,
  ReceivedProposalDetailResponse,
  ReceivedProposalsQuery,
  ReceivedProposalsResponse,
  RejectProposalBody,
  RejectProposalResponse,
} from "../types/proposals";

const PER_PAGE = 20;

// 1. Fetcher
const getReceivedProposals = async (params: ReceivedProposalsQuery) => {
  const { data } = await axiosInstance.get<ReceivedProposalsResponse>(
    "/v1/proposals/received",
    { params },
  );
  return data;
};

// 2. Hook with infinite query
export const useGetReceivedProposals = (
  getFilters: () => { proposal_state?: ProposalState },
) => {
  const [auth] = useAuthV2();

  return useInfiniteQuery(() => {
    const filters = getFilters();
    return {
      queryKey: [PROPOSAL_KEY, "received", auth.profile?.id, filters],
      queryFn: ({ pageParam = 1 }) =>
        getReceivedProposals({
          page: pageParam,
          per_page: PER_PAGE,
          proposal_state: filters.proposal_state,
          sort_order: "desc",
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage: ReceivedProposalsResponse) =>
        lastPage.pagination.nextPage,
      getPreviousPageParam: (firstPage: ReceivedProposalsResponse) =>
        firstPage.pagination.prevPage,
      enabled: !!auth.profile?.id,
    };
  });
};

// --- Single proposal detail ---

const getProposalDetail = async (proposalId: string) => {
  const { data } = await axiosInstance.get<ReceivedProposalDetailResponse>(
    `/v1/proposals/${proposalId}`,
  );
  return data;
};

export const createProposalDetailQueryOptions = (proposalId: string) => ({
  queryKey: [PROPOSAL_KEY, "detail", proposalId],
  queryFn: () => getProposalDetail(proposalId),
});

export const useGetProposalDetail = (proposalId: string) => {
  const [auth] = useAuthV2();
  return useQuery(() => ({
    ...createProposalDetailQueryOptions(proposalId),
    enabled: !!auth.profile?.id,
  }));
};

// --- Accept / Reject mutations (placeholder endpoints) ---

const acceptProposal = async (proposalId: string) => {
  const { data } = await axiosInstance.patch<AcceptProposalResponse>(
    `/v1/proposals/${proposalId}/accept`,
    {},
  );
  return data;
};

export const useAcceptProposal = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (proposalId: string) => acceptProposal(proposalId),
    onSuccess: (_data: AcceptProposalResponse, proposalId: string) => {
      queryClient.invalidateQueries({
        queryKey: [PROPOSAL_KEY, "detail", proposalId],
      });
      queryClient.invalidateQueries({
        queryKey: [PROPOSAL_KEY, "received"],
      });
    },
  }));
};

const confirmMeeting = async (
  proposalId: string,
  meeting_date: string | null,
) => {
  const { data } = await axiosInstance.patch<ConfirmMeetingResponse>(
    `/v1/proposals/${proposalId}/confirm-meeting`,
    { meeting_date },
  );
  return data;
};

export const useConfirmMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (params: { proposalId: string; meeting_date: string | null }) =>
      confirmMeeting(params.proposalId, params.meeting_date),
    onSuccess: (
      _data: ConfirmMeetingResponse,
      params: { proposalId: string; meeting_date: string | null },
    ) => {
      queryClient.invalidateQueries({
        queryKey: [PROPOSAL_KEY, "detail", params.proposalId],
      });
      queryClient.invalidateQueries({
        queryKey: [PROPOSAL_KEY, "received"],
      });
    },
  }));
};

const dismissMeeting = async (proposalId: string) => {
  const { data } = await axiosInstance.patch<ConfirmMeetingResponse>(
    `/v1/proposals/${proposalId}/confirm-meeting`,
    { meeting_date: null },
  );
  return data;
};

export const useDismissMeeting = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (proposalId: string) => dismissMeeting(proposalId),
    onSuccess: (_data: ConfirmMeetingResponse, proposalId: string) => {
      queryClient.invalidateQueries({
        queryKey: [PROPOSAL_KEY, "detail", proposalId],
      });
      queryClient.invalidateQueries({
        queryKey: [PROPOSAL_KEY, "received"],
      });
    },
  }));
};

const rejectProposal = async (proposalId: string, body: RejectProposalBody) => {
  const { data } = await axiosInstance.patch<RejectProposalResponse>(
    `/v1/proposals/${proposalId}/reject`,
    body,
  );
  return data;
};

export const useRejectProposal = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (params: { proposalId: string } & RejectProposalBody) =>
      rejectProposal(params.proposalId, {
        reason: params.reason,
        note: params.note,
      }),
    onSuccess: (
      _data: RejectProposalResponse,
      params: { proposalId: string } & RejectProposalBody,
    ) => {
      queryClient.invalidateQueries({
        queryKey: [PROPOSAL_KEY, "detail", params.proposalId],
      });
      queryClient.invalidateQueries({
        queryKey: [PROPOSAL_KEY, "received"],
      });
    },
  }));
};
